import React, { useState, useMemo, useEffect, useRef } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, mono, btnS, btnD } from "../../../lib/styles.js";
import { fmt, fmtDate, FMT_DATE_FULL, getFirstCaptureDate, exportCsv } from "../../../lib/utils.js";
import { JOB_STATUSES, JOB_STATUS_LABELS, JOB_STATUS_COLORS, JOB_STATUS_SUBLABEL } from "../../../lib/constants.js";
import { jobAccess } from "../lib/jobAccess.js";
import { jobRevenueParts, isRolledUpChangeOrderJob } from "../lib/revenue.js";

const RECENT_DAYS = 30;
const JSC = JOB_STATUS_COLORS(C);

export default function JobsList({ user, onOpenJob }) {
  const {
    jobs, trashedJobs, savedQuotes,
    restoreJob, permanentDeleteJob,
    jobsError, loaded,
  } = useData();

  const { canSeeMoney, canManageJobs } = jobAccess(user?.role);
  const isInitialLoad = !loaded && jobs.length === 0;

  const [view, setView] = useState("active");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("status");
  const [sortDir, setSortDir] = useState("asc");
  const [archiveYear, setArchiveYear] = useState("all");
  const [archiveClient, setArchiveClient] = useState("all");
  const [archiveSearch, setArchiveSearch] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // When money is hidden, never sort by the (hidden) revenue column.
  const effSortCol = (!canSeeMoney && sortCol === "revenue") ? "date" : sortCol;

  const today = new Date().toISOString().slice(0, 10);
  const recentCutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - RECENT_DAYS);
    return d.toISOString().slice(0, 10);
  }, []);

  // Decorate jobs once so filters, sorts, and row renders don't each call
  // getFirstCaptureDate() (which itself runs sortSessions() on every call).
  // _rev is the rolled-up project total (job + its change orders — the same
  // parts builder Insights and the Dashboard use); a CO child job whose parent
  // is live stays visible as a scheduled-work row but carries _rev null and
  // _coParent, so its money is never counted or summed twice.
  const decoratedJobs = useMemo(() => jobs.map(j => {
    const rolledUp = isRolledUpChangeOrderJob(j, jobs);
    const parent = rolledUp ? jobs.find(p => p.id === j.changeOrderOf) : null;
    return {
      ...j,
      _date: getFirstCaptureDate(j) || (j.createdAt || "").slice(0, 10) || "",
      _rev: rolledUp ? null : jobRevenueParts(j, savedQuotes, jobs).total,
      _coParent: parent ? (parent.jobId || parent.id) : null,
    };
  }), [jobs, savedQuotes]);

  const workingJobs = useMemo(() => decoratedJobs.filter(j => {
    if (["scheduled", "in_progress", "processing"].includes(j.status)) return true;
    return j._date >= recentCutoff;
  }), [decoratedJobs, recentCutoff]);

  const archivedJobs = useMemo(() => decoratedJobs.filter(j =>
    j.status === "delivered" || j.invoiced || j.paid
  ), [decoratedJobs]);

  const pipelineCounts = useMemo(() => ({
    scheduled:   workingJobs.filter(j => j.status === "scheduled").length,
    in_progress: workingJobs.filter(j => j.status === "in_progress").length,
    processing:  workingJobs.filter(j => j.status === "processing").length,
    delivered:   workingJobs.filter(j => j.status === "delivered").length,
    invoiced:    workingJobs.filter(j => j.invoiced && !j.paid).length,
    paid:        workingJobs.filter(j => j.paid).length,
  }), [workingJobs]);

  const filteredActive = useMemo(() => {
    let pool = workingJobs;
    if (clientFilter !== "all") pool = pool.filter(j => j.clientName === clientFilter);
    if (statusFilter === "invoiced") pool = pool.filter(j => j.invoiced && !j.paid);
    else if (statusFilter === "paid") pool = pool.filter(j => j.paid);
    else if (statusFilter !== "all") pool = pool.filter(j => j.status === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) pool = pool.filter(j =>
      (j.clientName || "").toLowerCase().includes(q) ||
      (j.jobId || "").toLowerCase().includes(q)
    );
    return sortJobs(pool, effSortCol, sortDir);
  }, [workingJobs, clientFilter, statusFilter, search, effSortCol, sortDir]);

  const filteredArchive = useMemo(() => {
    let pool = archivedJobs;
    if (archiveYear !== "all") pool = pool.filter(j => j._date.slice(0, 4) === archiveYear);
    if (archiveClient !== "all") pool = pool.filter(j => j.clientName === archiveClient);
    const q = archiveSearch.trim().toLowerCase();
    if (q) pool = pool.filter(j =>
      (j.clientName || "").toLowerCase().includes(q) ||
      (j.jobId || "").toLowerCase().includes(q)
    );
    return sortJobs(pool, effSortCol, sortDir);
  }, [archivedJobs, archiveYear, archiveClient, archiveSearch, effSortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "revenue" || col === "date" ? "desc" : "asc"); }
    setPage(1);
  };

  const handleRestore = (id) => {
    restoreJob(id);
    setConfirmDeleteId(null);
  };

  const handlePermanentDelete = (id) => {
    permanentDeleteJob(id);
    setConfirmDeleteId(null);
  };

  // Admin-only data dumps of all (non-trashed) jobs. CSV reuses the shared
  // exportCsv helper; JSON uses an object URL with a deferred revoke to avoid a
  // download-abort race in some browsers.
  const handleExportCsv = () => exportCsv(jobs, "nycap_jobs");
  const handleExportJson = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(jobs, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "nycap_jobs.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  if (isInitialLoad) {
    return (
      <div style={{ fontFamily: font, padding: 32, textAlign: "center", color: C.muted, fontSize: 13 }}>
        Loading jobs…
      </div>
    );
  }

  if (jobsError) {
    return (
      <div style={{ fontFamily: font, padding: 32, textAlign: "center", color: C.danger, fontSize: 13 }}>
        Failed to load jobs: {jobsError}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font }}>
      {/* Page header */}
      <div style={S.pageHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={S.pageTitle}>Jobs</div>
          <ViewToggle
            view={view}
            onChange={(v) => { setView(v); setPage(1); setConfirmDeleteId(null); }}
            counts={{ active: workingJobs.length, archive: archivedJobs.length, trash: trashedJobs.length }}
          />
        </div>
        {/* Both exports dump the whole jobs list — archival, not daily work — so
            they share one menu instead of two buttons in the page corner. */}
        {canManageJobs && (
          <ExportMenu onCsv={handleExportCsv} onJson={handleExportJson} />
        )}
      </div>

      {view === "active" && (
        <ActiveView
          canSeeMoney={canSeeMoney}
          today={today}
          workingJobs={workingJobs}
          pipelineCounts={pipelineCounts}
          statusFilter={statusFilter} setStatusFilter={(k) => { setStatusFilter(k); setPage(1); }}
          clientFilter={clientFilter} setClientFilter={(v) => { setClientFilter(v); setPage(1); }}
          search={search} setSearch={(v) => { setSearch(v); setPage(1); }}
          rows={filteredActive}
          sortCol={effSortCol} sortDir={sortDir} onSort={handleSort}
          page={page} setPage={setPage}
          perPage={perPage} setPerPage={(n) => { setPerPage(n); setPage(1); }}
          onOpenJob={onOpenJob}
        />
      )}

      {view === "archive" && (
        <ArchiveView
          canSeeMoney={canSeeMoney}
          archivedJobs={archivedJobs}
          rows={filteredArchive}
          archiveYear={archiveYear} setArchiveYear={(v) => { setArchiveYear(v); setPage(1); }}
          archiveClient={archiveClient} setArchiveClient={(v) => { setArchiveClient(v); setPage(1); }}
          archiveSearch={archiveSearch} setArchiveSearch={(v) => { setArchiveSearch(v); setPage(1); }}
          sortCol={effSortCol} sortDir={sortDir} onSort={handleSort}
          page={page} setPage={setPage}
          perPage={perPage} setPerPage={(n) => { setPerPage(n); setPage(1); }}
          onOpenJob={onOpenJob}
        />
      )}

      {view === "trash" && (
        <TrashView
          canSeeMoney={canSeeMoney}
          canManageJobs={canManageJobs}
          trashedJobs={trashedJobs}
          confirmDeleteId={confirmDeleteId}
          setConfirmDeleteId={setConfirmDeleteId}
          onRestore={handleRestore}
          onPermanentDelete={handlePermanentDelete}
        />
      )}
    </div>
  );
}

/* ─── Sort helper ─── */
const STATUS_ORDER = Object.fromEntries(JOB_STATUSES.map((s, i) => [s, i]));
// Paid/invoiced nudge a status forward without crossing into the next bucket,
// so a paid delivered job sorts after an unpaid delivered job.
const statusRank = (j) => {
  const base = STATUS_ORDER[j.status] ?? 99;
  return j.paid ? base + 0.3 : j.invoiced ? base + 0.2 : base;
};
const SORTERS = {
  client:  (a, b) => (a.clientName || "").localeCompare(b.clientName || ""),
  date:    (a, b) => (a._date || "").localeCompare(b._date || ""),
  status:  (a, b) => statusRank(a) - statusRank(b),
  jobId:   (a, b) => (a.jobId || "").localeCompare(b.jobId || ""),
  // Rolled-up totals; CO children (null _rev) sink below genuine $0 rows.
  revenue: (a, b) => (a._rev ?? -1) - (b._rev ?? -1),
};
function sortJobs(list, sortCol, sortDir) {
  const cmp = SORTERS[sortCol];
  if (!cmp) return list;
  const dir = sortDir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => cmp(a, b) * dir);
}

/* ═══════════════════════════════════════════════════════════════
   View toggle
   ═══════════════════════════════════════════════════════════════ */

function ViewToggle({ view, onChange, counts }) {
  const items = [
    { key: "active",  label: "Active",  count: counts.active },
    { key: "archive", label: "Archive", count: counts.archive },
    { key: "trash",   label: "Trash",   count: counts.trash },
  ];
  return (
    <div style={S.viewToggle}>
      {items.map(it => {
        const active = view === it.key;
        return (
          <button key={it.key} onClick={() => onChange(it.key)} style={{
            padding: "5px 14px",
            background: active ? C.text : "transparent",
            color: active ? "#FFF" : C.muted,
            border: "none",
            fontFamily: font,
            fontSize: 11.5,
            borderRadius: 4,
            cursor: "pointer",
            transition: "all 0.1s ease",
          }}>
            {it.label}
            <span style={{
              fontSize: 10,
              marginLeft: 5,
              fontVariantNumeric: "tabular-nums",
              color: active ? "rgba(255,255,255,0.6)" : C.muted,
            }}>{it.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Export menu
   ═══════════════════════════════════════════════════════════════ */

/* Page-local by repo convention (SavedQuotes has its own menus too); extract a
   shared component when a third consumer shows up. */
function ExportMenu({ onCsv, onJson }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const items = [
    { label: "Export CSV", onClick: onCsv },
    { label: "Export JSON", onClick: onJson },
  ];
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        style={{ ...btnS, padding: "6px 12px", fontSize: 11 }}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >Export ▾</button>
      {/* Plain buttons, not role="menu": the ARIA menu pattern promises arrow-key
          navigation and focus management this doesn't implement, and claiming it
          misleads screen-reader users more than the default button semantics do. */}
      {open && (
        <div className="jobs-menu" style={S.menu}>
          {items.map(it => (
            <button
              key={it.label}
              type="button"
              style={S.menuItem}
              onClick={() => { setOpen(false); it.onClick(); }}
            >{it.label}</button>
          ))}
        </div>
      )}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Active view
   ═══════════════════════════════════════════════════════════════ */

function ActiveView({
  canSeeMoney, today, workingJobs, pipelineCounts,
  statusFilter, setStatusFilter, clientFilter, setClientFilter, search, setSearch,
  rows, sortCol, sortDir, onSort, page, setPage, perPage, setPerPage, onOpenJob,
}) {
  const clients = useMemo(() =>
    Array.from(new Set(workingJobs.map(j => j.clientName).filter(Boolean))).sort()
  , [workingJobs]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = rows.slice((page - 1) * perPage, page * perPage);

  const chips = [];
  if (statusFilter !== "all") chips.push(
    <FilterChip key="s" label={`Status: ${JOB_STATUS_LABELS[statusFilter] || statusFilter}`} onClear={() => setStatusFilter("all")} />
  );
  if (clientFilter !== "all") chips.push(
    <FilterChip key="c" label={`Client: ${clientFilter}`} onClear={() => setClientFilter("all")} />
  );
  if (search.trim()) chips.push(
    <FilterChip key="q" label={`"${search.trim()}"`} onClear={() => setSearch("")} />
  );

  const pipelineItems = [
    { key: "scheduled",   label: "Scheduled",   count: pipelineCounts.scheduled,   color: JSC.scheduled,   milestone: false },
    { key: "in_progress", label: "In Progress", count: pipelineCounts.in_progress, color: JSC.in_progress, milestone: false },
    { key: "processing",  label: "Processing",  count: pipelineCounts.processing,  color: JSC.processing,  milestone: false },
    { key: "delivered",   label: "Delivered",   count: pipelineCounts.delivered,   color: JSC.delivered,   milestone: false },
    { key: "invoiced",    label: "Invoiced",    count: pipelineCounts.invoiced,    color: JSC.invoiced,    milestone: true },
    { key: "paid",        label: "Paid",        count: pipelineCounts.paid,        color: JSC.paid,        milestone: true },
  ].filter(it => canSeeMoney || !it.milestone);

  return (
    <div>
      {/* Pipeline strip — auto-fit collapses to 2-3 columns on narrow viewports */}
      <div style={S.pipeline}>
        {pipelineItems.map(it => (
          <PipelineTile
            key={it.key}
            active={statusFilter === it.key}
            label={it.label}
            count={it.count}
            sub={JOB_STATUS_SUBLABEL[it.key] || ""}
            color={it.color}
            onClick={() => setStatusFilter(statusFilter === it.key ? "all" : it.key)}
          />
        ))}
      </div>

      {/* Filter row */}
      <div style={S.filterRow}>
        <select style={S.select} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
          <option value="all">All clients</option>
          {clients.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <SearchInput value={search} onChange={setSearch} placeholder="Search client or job ID" />
        {chips}
        {chips.length > 0 && (
          <button onClick={() => { setStatusFilter("all"); setClientFilter("all"); setSearch(""); }} style={S.filterClear}>
            Clear all
          </button>
        )}
      </div>

      <JobsTable
        canSeeMoney={canSeeMoney} today={today}
        rows={pageRows}
        sortCol={sortCol} sortDir={sortDir} onSort={onSort}
        onOpenJob={onOpenJob}
        dateLabel="Date"
      />

      <PaginationFooter
        page={page} setPage={setPage}
        perPage={perPage} setPerPage={setPerPage}
        totalPages={totalPages} totalRows={total}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Archive view
   ═══════════════════════════════════════════════════════════════ */

function ArchiveView({
  canSeeMoney, archivedJobs, rows,
  archiveYear, setArchiveYear, archiveClient, setArchiveClient, archiveSearch, setArchiveSearch,
  sortCol, sortDir, onSort, page, setPage, perPage, setPerPage, onOpenJob,
}) {
  const clients = useMemo(() =>
    Array.from(new Set(archivedJobs.map(j => j.clientName).filter(Boolean))).sort()
  , [archivedJobs]);

  const years = useMemo(() => {
    const ys = new Set();
    archivedJobs.forEach(j => {
      const y = (j._date || "").slice(0, 4);
      if (y) ys.add(y);
    });
    return Array.from(ys).sort().reverse();
  }, [archivedJobs]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = rows.slice((page - 1) * perPage, page * perPage);

  // Rolled-up per project; CO children add 0 here — their money is inside the
  // parent's _rev, so the summary can never count a change order twice.
  const revenue = canSeeMoney ? rows.reduce((s, j) => s + (j._rev || 0), 0) : 0;
  const costs = canSeeMoney ? rows.reduce((s, j) => s + (j.productionCost || 0) + (j.contractorCost || 0), 0) : 0;
  const net = revenue - costs;
  const netPct = revenue > 0 ? Math.round((net / revenue) * 100) : 0;
  const scopeLabel = archiveYear === "all" ? "all time" : archiveYear;

  return (
    <div>
      {canSeeMoney && (
        <div style={S.summaryGrid}>
          <SummaryCard label="Revenue" value={fmtMoneyK(revenue)} sub={scopeLabel} />
          <SummaryCard label="Jobs" value={String(rows.length)} sub="closed out" />
          <SummaryCard label="Costs" value={fmtMoneyK(costs)} sub="production + contractor" />
          <SummaryCard label="Net" value={fmtMoneyK(net)} sub={`${netPct}% margin`} subColor={net >= 0 ? JSC.paid : C.danger} />
        </div>
      )}

      <div style={S.filterRow}>
        <select style={S.select} value={archiveYear} onChange={e => setArchiveYear(e.target.value)}>
          <option value="all">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select style={S.select} value={archiveClient} onChange={e => setArchiveClient(e.target.value)}>
          <option value="all">All clients</option>
          {clients.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <SearchInput value={archiveSearch} onChange={setArchiveSearch} placeholder="Search archive" />
      </div>

      <JobsTable
        canSeeMoney={canSeeMoney}
        rows={pageRows}
        sortCol={sortCol} sortDir={sortDir} onSort={onSort}
        onOpenJob={onOpenJob}
        dateLabel="Closed"
      />

      <PaginationFooter
        page={page} setPage={setPage}
        perPage={perPage} setPerPage={setPerPage}
        totalPages={totalPages} totalRows={total}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Trash view
   ═══════════════════════════════════════════════════════════════ */

function TrashView({ canSeeMoney, canManageJobs, trashedJobs, confirmDeleteId, setConfirmDeleteId, onRestore, onPermanentDelete }) {
  if (!trashedJobs.length) {
    return (
      <div style={{ ...S.tableWrap, padding: "48px 24px", textAlign: "center", color: C.muted }}>
        <div style={{ fontSize: 14, color: C.text, marginBottom: 6 }}>Trash is empty</div>
        <div style={{ fontSize: 11.5 }}>Deleted jobs land here. Restore or permanently delete them from this view.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={S.trashBanner}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: JSC.in_progress, flexShrink: 0 }} />
        Trashed jobs stay here until you permanently delete them. Restoring returns a job to Active.
      </div>

      <div style={S.tableWrap}>
        {trashedJobs.map(j => {
          const confirm = confirmDeleteId === j.id;
          const date = getFirstCaptureDate(j) || (j.createdAt || "").slice(0, 10) || "";
          return (
            <div key={j.id} className="jobs-trash-row" style={S.trashRow}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 400 }}>{j.clientName || "Untitled"}</div>
                <div style={S.trashMeta}>{j.jobId || "—"}{date ? ` · ${fmtDate(date)}` : ""}</div>
              </div>
              <div style={S.trashMeta}>{j.deletedAt ? `Deleted ${fmtDate(j.deletedAt.slice(0, 10), FMT_DATE_FULL)}` : ""}</div>
              {canSeeMoney && <div style={S.trashMeta}>{fmt(j.grossRevenue || 0)}</div>}
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                {confirm && canManageJobs ? (
                  <div style={S.confirmInline}>
                    <span style={{ fontSize: 10.5, color: C.danger }}>Delete forever?</span>
                    <button style={{ ...btnD, padding: "4px 10px", fontSize: 10.5 }} onClick={() => onPermanentDelete(j.id)}>Yes, delete</button>
                    <button style={{ ...btnS, padding: "4px 10px", fontSize: 10.5 }} onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                  </div>
                ) : (
                  <>
                    {canManageJobs && <button style={{ ...btnS, padding: "4px 10px", fontSize: 10.5 }} onClick={() => onRestore(j.id)}>Restore</button>}
                    {canManageJobs && (
                      <button style={{ ...btnD, padding: "4px 10px", fontSize: 10.5 }} onClick={() => setConfirmDeleteId(j.id)}>
                        Delete forever
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Shared: Jobs table
   ═══════════════════════════════════════════════════════════════ */

const BASE_COLS = [
  { key: "client", label: "Client", num: false },
  { key: "date",   label: "Date",   num: false },
  { key: "status", label: "Status", num: false },
  { key: "jobId",  label: "Job ID", num: false },
];
const REVENUE_COL = { key: "revenue", label: "Revenue", num: true };

function JobsTable({ canSeeMoney, today, rows, sortCol, sortDir, onSort, onOpenJob, dateLabel }) {
  const cols = useMemo(() => {
    const base = dateLabel
      ? BASE_COLS.map(c => c.key === "date" ? { ...c, label: dateLabel } : c)
      : BASE_COLS;
    return canSeeMoney ? [...base, REVENUE_COL] : base;
  }, [canSeeMoney, dateLabel]);

  return (
    <div className="jobs-table-wrap" style={S.tableWrap}>
      <div style={S.tableScroll}>
        <table className="jobs-table" style={S.table}>
          <thead>
            <tr>
              {cols.map(col => (
                <SortTh key={col.key} col={col.key} label={col.label} num={col.num}
                  sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="jobs-empty-row">
                <td colSpan={cols.length} style={{ padding: "48px 24px", textAlign: "center", color: C.muted }}>
                  <div style={{ fontSize: 14, color: C.text, marginBottom: 6 }}>No jobs match these filters</div>
                  <div style={{ fontSize: 11.5 }}>Adjust or clear the filters above to see more.</div>
                </td>
              </tr>
            ) : rows.map(j => (
              <JobRow key={j.id} job={j} canSeeMoney={canSeeMoney} today={today} onOpen={() => onOpenJob(j.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortTh({ col, label, num, sortCol, sortDir, onSort }) {
  const sorted = sortCol === col;
  const arrow = sorted ? (sortDir === "asc" ? "▲" : "▼") : "▲";
  return (
    <th onClick={() => onSort(col)} style={{
      background: "#FAFAF8",
      fontSize: 9.5,
      fontWeight: 400,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: sorted ? C.text : C.muted,
      padding: "10px 14px",
      textAlign: num ? "right" : "left",
      borderBottom: `1px solid ${C.border}`,
      cursor: "pointer",
      userSelect: "none",
      whiteSpace: "nowrap",
    }}>
      {label}
      <span style={{ marginLeft: 4, fontSize: 8, opacity: sorted ? 1 : 0.5, color: sorted ? C.text : C.muted }}>
        {arrow}
      </span>
    </th>
  );
}

function JobRow({ job, canSeeMoney, today, onOpen }) {
  const [hover, setHover] = useState(false);
  const meta = JSC[job.status] ? { color: JSC[job.status], label: JOB_STATUS_LABELS[job.status] || job.status } : { color: C.muted, label: job.status };
  const date = job._date || "";
  const upcoming = job.status === "scheduled" && date && date >= today;

  return (
    <tr
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        borderBottom: `1px solid ${C.border}`,
        background: hover ? C.bg : "transparent",
        cursor: "pointer",
        transition: "background 0.1s ease",
      }}>
      <td style={{ ...S.td, fontWeight: 400 }}>{job.clientName || "—"}</td>
      <td style={{ ...S.td, color: C.muted, fontSize: 11.5 }}>
        {date ? fmtDate(date, FMT_DATE_FULL) : "—"}
        {upcoming && (
          <span style={{ color: JSC.scheduled, fontSize: 9.5, marginLeft: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            upcoming
          </span>
        )}
      </td>
      <td style={S.td}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: 3.5, background: meta.color, flexShrink: 0 }} />
          <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 400, color: meta.color }}>
            {meta.label}
          </span>
        </span>
        {job.changeOrderOf && <MilestoneTag label="Change Order" color={C.muted} />}
        {job.invoiced && canSeeMoney && <MilestoneTag label="Invoiced" color={JSC.invoiced} />}
        {job.paid && canSeeMoney && <MilestoneTag label="Paid" color={JSC.paid} />}
      </td>
      <td style={{ ...S.td, fontFamily: mono, fontSize: 11.5, color: C.muted }}>{job.jobId || "—"}</td>
      {canSeeMoney && (
        <td style={{ ...S.td, textAlign: "right", fontFamily: mono, fontSize: 12 }}>
          {job._coParent
            ? <span style={{ fontSize: 10.5, color: C.muted }}>in {job._coParent}</span>
            : fmt(job._rev ?? job.grossRevenue ?? 0)}
        </td>
      )}
    </tr>
  );
}

function MilestoneTag({ label, color }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "1px 6px",
      borderRadius: 4,
      fontSize: 9,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      marginLeft: 4,
      verticalAlign: "middle",
      background: `${color}1F`,
      color,
    }}>{label}</span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Shared atoms
   ═══════════════════════════════════════════════════════════════ */

function PipelineTile({ active, label, count, sub, color, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover && !active ? C.bg : C.surface,
        border: "1px solid " + (active ? "transparent" : hover ? "#D0CDC8" : C.border),
        boxShadow: active ? `inset 0 0 0 1.5px ${color}` : "none",
        borderRadius: 8,
        padding: "12px 14px",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: font,
        transition: "all 0.12s ease",
      }}>
      <div style={{
        fontSize: 9.5,
        fontWeight: 400,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: active ? color : C.muted,
        marginBottom: 6,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: color, flexShrink: 0 }} />
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{count}</div>
      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{sub}</div>
    </button>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 8px",
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      fontSize: 11,
      color: C.muted,
    }}>
      {label}
      <span onClick={onClear} style={{ cursor: "pointer", fontSize: 12, lineHeight: 1, color: C.muted }}>×</span>
    </span>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
      <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: C.muted, pointerEvents: "none", fontSize: 12 }}>⌕</span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "7px 12px 7px 28px",
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 6,
          fontFamily: font,
          fontSize: 12,
          color: C.text,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

function SummaryCard({ label, value, sub, subColor }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: "14px 16px",
    }}>
      <div style={{
        fontSize: 9.5,
        fontWeight: 400,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: C.muted,
        marginBottom: 6,
      }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: subColor || C.muted, marginTop: 3 }}>{sub}</div>
    </div>
  );
}

function PaginationFooter({ page, setPage, perPage, setPerPage, totalPages, totalRows }) {
  const showPager = totalPages > 1;
  const showing = totalRows === 0
    ? "0"
    : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, totalRows)}`;

  return (
    <div style={S.pagination}>
      <span style={{ fontVariantNumeric: "tabular-nums", marginRight: "auto", fontSize: 11, color: C.muted }}>
        Showing {showing} of {totalRows}
      </span>
      {showPager && (
        <>
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            style={{ ...btnS, padding: "5px 12px", fontSize: 11, opacity: page === 1 ? 0.4 : 1, pointerEvents: page === 1 ? "none" : "auto" }}
          >‹ Previous</button>
          <span style={{ fontSize: 11, color: C.muted, fontVariantNumeric: "tabular-nums" }}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            style={{ ...btnS, padding: "5px 12px", fontSize: 11, opacity: page === totalPages ? 0.4 : 1, pointerEvents: page === totalPages ? "none" : "auto" }}
          >Next ›</button>
        </>
      )}
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
        <span>Rows</span>
        <select
          value={perPage}
          onChange={e => setPerPage(parseInt(e.target.value, 10) || 20)}
          style={{ ...S.select, minWidth: 64, padding: "5px 8px", fontSize: 11.5, fontVariantNumeric: "tabular-nums" }}
        >
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </label>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Formatters
   ═══════════════════════════════════════════════════════════════ */

function fmtMoneyK(n) {
  if (!n) return "$0";
  if (n >= 1000) return "$" + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k";
  return "$" + n;
}

/* ═══════════════════════════════════════════════════════════════
   Styles
   ═══════════════════════════════════════════════════════════════ */

const S = {
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    zIndex: 30,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(26,26,26,0.12)",
    minWidth: 160,
    padding: 5,
  },
  menuItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "7px 11px",
    border: "none",
    background: "transparent",
    fontFamily: font,
    fontSize: 12,
    color: C.text,
    borderRadius: 5,
    cursor: "pointer",
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: C.text,
  },
  viewToggle: {
    display: "inline-flex",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: 2,
    gap: 2,
  },
  pipeline: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 8,
    marginBottom: 16,
  },
  filterRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 12,
    flexWrap: "wrap",
  },
  select: {
    padding: "7px 12px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 12,
    color: C.text,
    cursor: "pointer",
    minWidth: 160,
    outline: "none",
  },
  filterClear: {
    fontSize: 10.5,
    color: C.muted,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "4px 8px",
    textDecoration: "underline",
    fontFamily: font,
  },
  tableWrap: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  tableScroll: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontVariantNumeric: "tabular-nums",
  },
  td: {
    padding: "11px 14px",
    fontSize: 12.5,
    color: C.text,
    verticalAlign: "middle",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    fontSize: 11,
    color: C.muted,
    flexWrap: "wrap",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  trashBanner: {
    background: `${JSC.in_progress}14`,
    border: `1px solid ${JSC.in_progress}40`,
    borderRadius: 6,
    padding: "10px 14px",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 11.5,
    color: C.text,
  },
  trashRow: {
    display: "grid",
    gridTemplateColumns: "1fr 160px 140px auto",
    gap: 14,
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  trashMeta: {
    fontSize: 11.5,
    color: C.muted,
  },
  confirmInline: {
    display: "inline-flex",
    gap: 6,
    alignItems: "center",
    background: "rgba(239,68,68,0.06)",
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid rgba(239,68,68,0.2)",
  },
};
